import {
  AMAZON_AF_DP,
  APPSFLYER_AF_SUB1,
  APPSFLYER_DIRECT_CLICK_PARAMETERS,
  APPSFLYER_ONE_LINK_URL,
  APPSFLYER_PARAMETERS,
  APPSFLYER_PLATFORM_BY_BACKEND_KEY,
  FIRE_TV_PLATFORM_KEY,
} from '~/shared/constants'
import { getStoreUrl } from './appOpen'

/**
 * Region id the app can never resolve — `/api/web/live-channels/v2/regions`
 * only ever returns positive ids, and `0` is our own `noRegionSelection`
 * sentinel. The unresolvable route lands the app on Home, the Fallback
 * [mobile] Deeplinking documents for `/live/region/{regionid}` (BR-02).
 */
const NO_REGION_ID = 0

/**
 * AppsFlyer deep_link_value for Start Watching: the Live TV All channels
 * preview, where the app autoplays the first channel. Canonical route from
 * [mobile] Deeplinking → Deeplink mapping: `/live/region/{regionid}/preview`.
 *
 * Never empty — an omitted `deep_link_value` leaves AppsFlyer with no URI to
 * route (`UDL: FOUND but no routable URI in payload`) and the app is not opened
 * at all, which is the OVP-3037 defect.
 */
export function buildStartWatchingDeepLinkValue(
  regionId?: number | string | null
): string {
  return `live/region/${regionId || NO_REGION_ID}/preview`
}

export function getAppsFlyerPlatformByKey(
  backendPlatformKey: string
): string | undefined {
  return APPSFLYER_PLATFORM_BY_BACKEND_KEY[backendPlatformKey]
}

interface PlatformDirectClickInput {
  /** Backend platform key (ios, android, fireTv, …) */
  platform: string
  link: string
  afPlatform: string
  appId: string
}

/** OneLink with page attribution plus an optional deferred deep link. */
function generateOneLinkWithDeepLinkValue(
  deepLinkValue: string | null,
  afSub1: string | null
): string | null {
  if (
    typeof window === 'undefined' ||
    !window.AF_SMART_SCRIPT?.generateOneLinkURL
  ) {
    return null
  }

  const afParameters: Record<string, unknown> = { ...APPSFLYER_PARAMETERS }
  if (deepLinkValue) {
    afParameters.deepLinkValue = { defaultValue: deepLinkValue }
  }
  if (afSub1) {
    afParameters.afSub1 = { defaultValue: afSub1 }
  }

  const result = window.AF_SMART_SCRIPT.generateOneLinkURL({
    oneLinkURL: APPSFLYER_ONE_LINK_URL,
    afParameters,
  })

  return result?.clickURL ?? null
}

/** Install banner redirect: OneLink with attribution, optional deferred deep link. */
export function getInstallBannerClickUrl(
  deepLinkValue?: string | null,
  afSub1?: string | null
): string {
  const afSub1Value = afSub1 ?? null

  // 1. Fresh OneLink with content context (region/category/channel)
  if (deepLinkValue) {
    const urlWithDeepLink = generateOneLinkWithDeepLinkValue(
      deepLinkValue,
      afSub1Value
    )
    if (urlWithDeepLink) return urlWithDeepLink
  }

  // 2. Base OneLink from page load (UTM only, no deep_link_value)
  if (typeof window !== 'undefined') {
    const cached = window.__AF_INSTALL_ONE_LINK_URL__
    if (cached) {
      return afSub1Value
        ? appendOneLinkSearchParams(cached, { af_sub1: afSub1Value })
        : cached
    }
  }

  // 3. Regenerate base OneLink if cache missing (e.g. click before script onload)
  const baseOneLink = generateOneLinkWithDeepLinkValue(null, afSub1Value)
  if (baseOneLink) return baseOneLink

  // 4. Direct store URL
  return getStoreUrl()
}

/**
 * Email Verified → Start Watching: OneLink that opens the installed app on the
 * Live TV module and sends every device without the app to web Live TV.
 * Unlike getInstallBannerClickUrl it never falls back to a store.
 * `webFallbackUrl` must be absolute — AppsFlyer redirects to it verbatim.
 */
export function getStartWatchingClickUrl(
  deepLinkValue: string | null,
  webFallbackUrl: string,
  afSub1: string = APPSFLYER_AF_SUB1.emailVerified
): string {
  const oneLink = generateOneLinkWithDeepLinkValue(deepLinkValue, afSub1)
  if (!oneLink) return webFallbackUrl

  return appendOneLinkSearchParams(oneLink, {
    // app not installed: web Live TV instead of the store the template points at
    af_r: webFallbackUrl,
    af_ios_url: webFallbackUrl,
    af_android_url: webFallbackUrl,
    af_web_dp: webFallbackUrl,
  })
}

function appendOneLinkSearchParams(
  clickUrl: string,
  params: Record<string, string>
): string {
  const url = new URL(clickUrl)
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value)
  }
  return url.toString()
}

function generateAttributedOneLink(
  afParameters: typeof APPSFLYER_DIRECT_CLICK_PARAMETERS
): string | null {
  if (
    typeof window === 'undefined' ||
    !window.AF_SMART_SCRIPT?.generateOneLinkURL
  ) {
    return null
  }

  const result = window.AF_SMART_SCRIPT.generateOneLinkURL({
    oneLinkURL: APPSFLYER_ONE_LINK_URL,
    afParameters,
  })

  return result?.clickURL ?? null
}

/**
 * Amazon Appstore is not supported by generateDirectClickURL.
 * Use OneLink with af_dp (Amazon app URI) and af_r (web fallback).
 */
function generateAmazonStoreOneLink(webFallbackUrl: string): string | null {
  const baseOneLink = generateAttributedOneLink(
    APPSFLYER_DIRECT_CLICK_PARAMETERS
  )
  if (!baseOneLink) return null

  return appendOneLinkSearchParams(baseOneLink, {
    af_dp: AMAZON_AF_DP,
    af_r: webFallbackUrl,
  })
}

/**
 * Generates a platform-specific Direct Click URL that always redirects to the
 * target store regardless of the user's current device.
 * Returns null if required backend fields are missing or the script hasn't loaded.
 */
function generateDirectClickLink(
  input: PlatformDirectClickInput | null | undefined
): string | null {
  if (
    !input?.link ||
    !input.afPlatform ||
    !input.appId ||
    typeof window === 'undefined' ||
    !window.AF_SMART_SCRIPT?.generateDirectClickURL
  ) {
    return null
  }

  const result = window.AF_SMART_SCRIPT.generateDirectClickURL({
    afParameters: APPSFLYER_DIRECT_CLICK_PARAMETERS,
    platform: input.afPlatform,
    app_id: input.appId,
    redirectURL: input.link,
  })

  return result?.clickURL ?? null
}

/** AppsFlyer direct click URL when available, otherwise backend store link. */
export function getPlatformClickUrl(
  input: PlatformDirectClickInput | null | undefined,
  afSub1?: string
): string | undefined {
  if (!input?.link || input?.link?.includes('#')) return undefined

  let url: string | undefined
  if (input.platform === FIRE_TV_PLATFORM_KEY) {
    url = generateAmazonStoreOneLink(input.link) || input.link
  } else {
    url = generateDirectClickLink(input) || input.link
  }

  if (url && afSub1) {
    return appendOneLinkSearchParams(url, { af_sub1: afSub1 })
  }
  return url
}
